import { ArrowDown, ArrowUp } from 'lucide-react'
import { type ReactNode } from 'react'

import {
  effectiveOrder,
  INFO_LINE_LABELS,
  type InfoLine,
} from '@aeris/printing'
import { Button } from '@aeris/ui'

/**
 * A ordem das linhas de dados do papel — F160.
 *
 * O dono pediu *"a possibilidade de alterar a ordem das linhas"* no editor. A
 * F149 tinha deixado a ordem inteira em código; esta tela abre só as linhas de
 * dados do topo, e diz o que continua fixo — senão alguém procura como pôr o
 * total antes dos itens e conclui que o editor está incompleto.
 *
 * Subir e descer por botão, e não arrastar: arrastar não se faz pelo teclado,
 * e cada botão diz qual linha move e para onde, para o leitor de tela.
 */
export const InfoOrderEditor = ({
  order,
  disabled,
  onMove,
}: {
  order: readonly InfoLine[]
  disabled: boolean
  onMove: (line: InfoLine, by: -1 | 1) => void
}): ReactNode => {
  const lines = effectiveOrder(order)

  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="text-(length:--text-sm) font-(--weight-semibold) text-(--text-primary)">
        Ordem das linhas de dados
      </legend>
      <p className="text-(length:--text-xs) text-(--text-secondary)">
        Cada papel usa as linhas que tem — o recibo não tem "Válido até", o
        orçamento não tem operador — e uma linha sem valor não sai. O aviso do
        topo, os itens, o total e os pagamentos ficam sempre na mesma ordem: um
        papel com o total antes dos itens se lê errado no balcão.
      </p>
      <ol className="flex flex-col gap-1">
        {lines.map((line, index) => (
          <li
            key={line}
            className="flex items-center justify-between gap-2 rounded-(--radius-sm) border border-(--border-subtle) px-2 py-1 text-(length:--text-sm)"
          >
            <span>{INFO_LINE_LABELS[line]}</span>
            <span className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                disabled={disabled || index === 0}
                aria-label={`Subir ${INFO_LINE_LABELS[line]}`}
                leadingIcon={<ArrowUp aria-hidden="true" className="h-4 w-4" />}
                onClick={() => {
                  onMove(line, -1)
                }}
              />
              <Button
                variant="ghost"
                size="sm"
                disabled={disabled || index === lines.length - 1}
                aria-label={`Descer ${INFO_LINE_LABELS[line]}`}
                leadingIcon={
                  <ArrowDown aria-hidden="true" className="h-4 w-4" />
                }
                onClick={() => {
                  onMove(line, 1)
                }}
              />
            </span>
          </li>
        ))}
      </ol>
    </fieldset>
  )
}
